import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Reveal from '../components/Reveal/Reveal';
import api from '../services/api';
import { formatDateTimeInParis } from '../utils/dateTime';
import { EMAIL_INPUT_PATTERN, EMAIL_INPUT_TITLE, isValidEmailAddress } from '../utils/emailValidation';
import './TransferStatus.css';

const STATUS_META = {
  pending: {
    label: 'En attente',
    tone: 'pending',
    description: 'Votre dossier est en cours de traitement par notre equipe.'
  },
  accepted: {
    label: 'Accepte',
    tone: 'accepted',
    description: 'Votre demande a ete acceptee.'
  },
  rejected: {
    label: 'Refuse',
    tone: 'rejected',
    description: 'Votre demande a ete rejetee. Contactez-nous si vous souhaitez plus de details.'
  },
  awaiting_response_from_user: {
    label: "En attente de reponse de l'utilisateur",
    tone: 'waiting',
    description: 'Nous attendons une action ou un document complementaire de votre part.'
  }
};

const normalizeStatus = (value) => {
  const status = String(value || '').trim().toLowerCase();

  if (['accepted', 'approve', 'approved'].includes(status)) {
    return 'accepted';
  }

  if (['rejected', 'reject', 'denied', 'disapproved'].includes(status)) {
    return 'rejected';
  }

  if (
    [
      'awaiting_response_from_user',
      'awaiting_response',
      'awaiting user response',
      'pending_user_action',
    ].includes(status)
  ) {
    return 'awaiting_response_from_user';
  }

  return 'pending';
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  return formatDateTimeInParis(value, { locale: 'fr-FR', fallback: 'N/A' });
};

const getHashQueryString = (rawHash = '') => {
  const normalized = String(rawHash || '').replace(/^#/, '');
  if (!normalized) return '';

  const queryStart = normalized.indexOf('?');
  if (queryStart >= 0) return normalized.slice(queryStart + 1);

  // Some email/security wrappers preserve query-like hash fragments.
  if (normalized.includes('=')) return normalized;
  return '';
};

const TransferStatus = () => {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [ticketCode, setTicketCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const autoLookupKeyRef = useRef('');

  const statusView = useMemo(() => {
    const normalized = normalizeStatus(result?.status);
    return STATUS_META[normalized] || STATUS_META.pending;
  }, [result]);

  const resolvedResult = useMemo(() => {
    if (!result) return null;
    return {
      referenceNumber: result.reference_number || result.referenceNumber || null,
      updatedAt: result.updated_at || result.updatedAt || null,
      submittedAt: result.submitted_at || result.submittedAt || null,
      applicantName: result.applicant_name || result.applicantName || null,
      formName: result.form_name || result.formName || null,
      requestTabName: result.request_tab_name || result.requestTabName || null,
    };
  }, [result]);

  const fetchStatus = useCallback(async ({ lookupEmail, lookupTicketCode }) => {
    setError('');
    setResult(null);

    const normalizedEmail = String(lookupEmail || '').trim().toLowerCase();
    const normalizedTicket = String(lookupTicketCode || '').trim().toUpperCase();

    if (!normalizedEmail || !normalizedTicket) {
      setError('Veuillez renseigner votre email et votre numero de ticket.');
      return;
    }

    try {
      setLoading(true);
      const response = await api.forms.getRequestStatus({
        email: normalizedEmail,
        ticketCode: normalizedTicket,
      });
      setResult(response?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Impossible de recuperer le statut pour le moment.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(getHashQueryString(location.hash));
    const paramSources = [searchParams, hashParams];

    const readParam = (...keys) => {
      for (const source of paramSources) {
        for (const key of keys) {
          const direct = source.get(key);
          if (direct !== null) return direct;

          const htmlEncoded = source.get(`amp;${key}`);
          if (htmlEncoded !== null) return htmlEncoded;
        }
      }
      return null;
    };

    const emailFromQuery = String(readParam('email', 'mail', 'e') || '').trim().toLowerCase();
    const ticketFromQuery = String(
      readParam('ticketCode', 'ticket', 'referenceNumber', 'reference', 'ticket_code') || ''
    ).trim().toUpperCase();

    if (!emailFromQuery || !ticketFromQuery) return;

    setEmail(emailFromQuery);
    setTicketCode(ticketFromQuery);

    const rawAutoFetch = readParam('autofetch', 'autoFetch', 'auto_search');
    const autoFetchValue = String(rawAutoFetch == null || rawAutoFetch === '' ? '1' : rawAutoFetch)
      .trim()
      .toLowerCase();
    const shouldAutoFetch = !['0', 'false', 'no'].includes(autoFetchValue);
    const lookupKey = `${emailFromQuery}|${ticketFromQuery}|${shouldAutoFetch}`;

    if (autoLookupKeyRef.current === lookupKey) return;
    autoLookupKeyRef.current = lookupKey;

    if (shouldAutoFetch) {
      fetchStatus({
        lookupEmail: emailFromQuery,
        lookupTicketCode: ticketFromQuery,
      });
    }
  }, [fetchStatus, location.hash, location.search]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValidEmailAddress(email)) {
      setError('Veuillez saisir une adresse email valide contenant @.');
      return;
    }
    fetchStatus({
      lookupEmail: email,
      lookupTicketCode: ticketCode,
    });
  };

  return (
    <div className="transfer-page">
      <section className="transfer-hero">
        <Reveal as="div" className="hero-content" variant="up">
          <h1>Statut des demandes</h1>
          <p>Consultez le statut de n'importe quelle demande avec votre email et votre numero de ticket.</p>
        </Reveal>
      </section>

      <section className="transfer-content">
        <div className="container">
          <Reveal as="div" className="transfer-card" variant="up" delay={60}>
            <h2 className="transfer-card-title">Suivre mon dossier</h2>
            <p className="transfer-card-subtitle">Statuts possibles : Accepté, En attente, Refusé, En attente de réponse de l'utilisateur.</p>

            <form className="transfer-form" onSubmit={handleSubmit}>
              <label className="transfer-field">
                <span>Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  required
                  pattern={EMAIL_INPUT_PATTERN}
                  title={EMAIL_INPUT_TITLE}
                />
              </label>

              <label className="transfer-field">
                <span>Numero de ticket</span>
                <input
                  type="text"
                  value={ticketCode}
                  onChange={(e) => setTicketCode(e.target.value)}
                  placeholder="TICKET-00000000"
                  required
                />
              </label>

              <button type="submit" className="transfer-submit" disabled={loading}>
                {loading ? 'Verification...' : 'Verifier mon statut'}
              </button>
            </form>

            {error && <div className="transfer-error">{error}</div>}

            {result && (
              <div className={`transfer-status-panel is-${statusView.tone}`}>
                <div className="transfer-status-head">
                  <span className={`transfer-status-dot is-${statusView.tone}`} />
                  <h3>{statusView.label}</h3>
                </div>

                <p className="transfer-status-description">{statusView.description}</p>

                <div className="transfer-status-grid">
                  <div>
                    <span className="transfer-status-label">Référence</span>
                    <strong>{resolvedResult?.referenceNumber || 'N/A'}</strong>
                  </div>
                  <div>
                    <span className="transfer-status-label">Derniere mise a jour</span>
                    <strong>{formatDateTime(resolvedResult?.updatedAt)}</strong>
                  </div>
                  <div>
                    <span className="transfer-status-label">Soumis le</span>
                    <strong>{formatDateTime(resolvedResult?.submittedAt)}</strong>
                  </div>
                  <div>
                    <span className="transfer-status-label">Demandeur</span>
                    <strong>{resolvedResult?.applicantName || 'N/A'}</strong>
                  </div>
                  <div>
                    <span className="transfer-status-label">Formulaire</span>
                    <strong>{resolvedResult?.formName || 'N/A'}</strong>
                  </div>
                  <div>
                    <span className="transfer-status-label">Categorie</span>
                    <strong>{resolvedResult?.requestTabName || 'N/A'}</strong>
                  </div>
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default TransferStatus;
